import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { GQLCategoryFragment } from '@theprintbar/graphql';
import { WebRoutes, cn } from '@theprintbar/shared';
import { CleanLink } from './ProductFilter/CleanLink';
import { ProductQueryKeys, ProductSearchQuery, getProductSearchURL } from './ProductFilter/productSearch';

export interface CategoryListProps {
	category?: GQLCategoryFragment | null;
	categories?: GQLCategoryFragment[] | null;
	params: ProductSearchQuery;
	store?: any;
}

export const CategoryList = function ({ category, categories, params, store }: CategoryListProps) {
	if (!categories) return null;

	const sortedCategories = categories.sort((a, b) => a.order - b.order);

	const activeCategoryCode = category?.code || params?.category?.[params.category.length - 1];

	const parents = sortedCategories.filter(cat => !cat.parents?.length);

	const categoryLink = (code: string, slug: string) =>
		store?.id
			? {
					to: getProductSearchURL(ProductQueryKeys.Category, code, { category: params.category }),
					filteredTo: getProductSearchURL(ProductQueryKeys.Category, code, params),
			  }
			: { to: WebRoutes.Collection(slug), filteredTo: WebRoutes.Collection(slug, params) };

	return (
		<div>
			<div className="flex justify-between items-end h-16 border-b border-blue mb-4 pb-4">
				<h5 className="font-display font-light text-xl">Categories</h5>
				{!!category && (
					<CleanLink
						to={store ? store.id + WebRoutes.Products() : WebRoutes.Products()}
						filteredTo={store ? store.id + WebRoutes.Products(params) : WebRoutes.Products(params)}
						className="flex items-center gap-2 text-sm"
					>
						All Products
						<ArrowUpRightIcon className="w-3 h-3" />
					</CleanLink>
				)}
			</div>
			<div className="pl-5">
				<ul>
					{parents.map(({ name, code, slug }) => {
						const active = activeCategoryCode === code;
						const children = categories.filter(c => c.parents?.some(p => p.code === code));
						const activeChild = children?.some(child => activeCategoryCode === child.code);
						const activeGrandChild = children?.some(child =>
							child.children?.some(grandChild => activeCategoryCode === grandChild.code),
						);
						const expanded = active || activeChild || activeGrandChild;

						return (
							<li className={cn('py-1', expanded && `negative`)} key={name}>
								<CleanLink
									end
									className={cn(active ? 'active underline' : 'group group-hover:underline')}
									{...categoryLink(code, slug)}
								>
									{name}
								</CleanLink>
								{expanded && children?.length ? (
									<ul className="pl-4">
										{children.map(child => {
											const childActive = activeCategoryCode === child.code;
											const grandChildren = categories.filter(c => c.parents?.some(p => p.code === child.code));
											const grandChildActive = grandChildren?.some(
												grandChild => activeCategoryCode === grandChild.code,
											);
											const childExpanded = childActive || grandChildActive;
											return (
												<li
													className={cn('py-1', childExpanded && `negative`, !grandChildren?.length && `blank`)}
													key={child.name}
												>
													<CleanLink
														end
														className={cn('group', childActive ? 'active underline' : 'group-hover:underline')}
														{...categoryLink(child.code, child.slug)}
													>
														{child.name}
													</CleanLink>
													{child.children?.length ? (
														<ul className="pl-4">
															{childExpanded &&
																child.children?.map(grandChild => {
																	const isActive = activeCategoryCode === grandChild.code;
																	const cat = categories.find(c => c.code === grandChild.code);
																	return cat ? (
																		<li
																			className={cn(`py-1 blank group-hover:underline`)}
																			key={grandChild.name}
																		>
																			<CleanLink
																				end
																				className={cn(
																					isActive ? 'underline active cursor-default' : 'group',
																				)}
																				{...categoryLink(cat.code, cat.slug)}
																			>
																				{grandChild.name}
																			</CleanLink>
																		</li>
																	) : null;
																})}
														</ul>
													) : null}
												</li>
											);
										})}
									</ul>
								) : null}
							</li>
						);
					})}
				</ul>
			</div>
		</div>
	);
};
